<script setup lang="ts">
import type { DirectMessageParticipants } from "../directMessageIdentity";
import { computed } from "vue";
import {
  UiText,
  UiAvatar,
  UiAccordion,
  UiIcon,
  generateInitials,
} from "@shopexperts/shared";
import type { ChatMessage, ParticipantDisplayMap, ProjectSide, RawQuoteRecord } from "@/v2Projects/types";
import { directMessageIdentity } from "../directMessageIdentity";
import ProjectCreatedMessageCard from "./ProjectCreatedMessageCard.vue";
import QuoteDetailStrip from "./QuoteDetailStrip.vue";
import QuoteMessageCard from "./QuoteMessageCard.vue";
import {
  formatQuotePaymentStatusLabel,
  isInternalQuotePayment,
} from "@/v2Projects/utils/quotePresentation";

const props = defineProps<{
  message: ChatMessage;
  isOwn: boolean;
  side: ProjectSide;
  showSender: boolean;
  participantsDisplay: ParticipantDisplayMap | null;
  directParticipants?: DirectMessageParticipants;
  quote?: RawQuoteRecord | null;
  expertRole?: string | null;
  isHighlighted?: boolean;
  autoOpenProposalDetails?: boolean;
  rejectQuote?: (quoteId: number) => Promise<void>;
  viewQuote?: (quoteId: number) => Promise<void>;
}>();

const sender = computed(() => {
  const snap = props.message.sender_snapshot;
  if (!snap) return { name: "System", avatar: undefined, initials: "S" };

  const live = directMessageIdentity(snap, props.directParticipants);
  if (live) return { name: live.name, avatar: live.avatar_url ?? undefined, initials: generateInitials(live.name) };

  if (snap.user_display_name) {
    return {
      name: snap.user_display_name,
      avatar: snap.user_avatar,
      initials: generateInitials(snap.user_display_name),
    };
  }

  if (props.participantsDisplay) {
    const user = props.participantsDisplay.users.find((u) =>
      u.id === snap.user_id,
    );
    if (user) {
      return {
        name: user.display_name || user.first_name || user.email || `User #${snap.user_id}`,
        avatar: user.avatar ?? undefined,
        initials: generateInitials(user.display_name || user.first_name || "U"),
      };
    }
  }

  const fallback = [snap.user_first_name, snap.user_last_name]
    .filter(Boolean)
    .join(" ") || snap.user_email;

  return {
    name: fallback || (props.directParticipants ? "Loading sender…" : `User #${snap.user_id}`),
    avatar: snap.user_avatar,
    initials: generateInitials(fallback || "U"),
  };
});

const clientDisplayName = computed(() => {
  if (props.side === "client") return "You";
  if (!props.participantsDisplay) return "The client";

  const clientParticipant = props.participantsDisplay.participants.find(
    (participant) => participant.participant_group === "client" && participant.is_active,
  );
  if (!clientParticipant) return "The client";

  const clientUser = props.participantsDisplay.users.find((user) =>
    user.organization_ids.includes(clientParticipant.org_id),
  );
  if (clientUser) {
    return clientUser.display_name || clientUser.first_name || clientUser.email || "The client";
  }

  const clientOrg = props.participantsDisplay.organizations.find(
    (org) => org.id === clientParticipant.org_id,
  );
  return clientOrg?.name || "The client";
});

const noteLabel = computed(() => {
  if (props.message.kind === "admin_note") return "Admin Note";
  if (props.message.kind === "personal_note") return "Personal only";
  if (props.message.kind === "group_note") {
    const group = props.message.sender_snapshot?.participant_group;
    return group === "expert" ? "Experts only" : "Clients only";
  }
  return null;
});

const isEmailReply = computed(() => props.message.origin?.kind === "email_reply");

const isNote = computed(
  () =>
    props.message.kind === "personal_note" ||
    props.message.kind === "group_note" ||
    props.message.kind === "admin_note",
);
const isSystem = computed(() => props.message.kind === "system_notification");
const isQuote = computed(() => props.message.kind === "quote");
const hasHtml = computed(() => props.message.content_format === "html"
  || (props.message.content_format === undefined && /<\/?[a-z][\s\S]*>/i.test(props.message.content ?? "")));
const effectiveQuotePrice = computed(() => {
  if (props.quote) {
    return {
      amount_in_cents: props.quote.amount_cents,
      currency: props.quote.currency,
      quantity: props.quote.quantity,
      interval: props.quote.interval,
    };
  }
  return props.message.price ?? null;
});
const quoteStrip = computed(() => {
  if (!effectiveQuotePrice.value) return null;
  return {
    amount_cents: effectiveQuotePrice.value.amount_in_cents,
    currency: effectiveQuotePrice.value.currency,
    quantity: effectiveQuotePrice.value.quantity,
    interval: effectiveQuotePrice.value.interval,
    delivery_after_days: props.quote?.delivery_after_days ?? props.message.delivery_after_days ?? null,
    includes_weekends: props.quote?.includes_weekends ?? props.message.includes_weekends ?? null,
    hours_bank_expires_after_days:
      props.quote?.hours_bank_expires_after_days ??
      props.message.latest_hours_bank_expires_after_days ??
      props.message.hours_bank_expires_after_days ??
      null,
    hours_bank_expiration_terms:
      props.quote?.hours_bank_expiration_terms ??
      props.message.latest_hours_bank_expiration_terms ??
      props.message.hours_bank_expiration_terms ??
      null,
    expires_at: null,
  };
});

const effectivePaymentMode = computed(() => props.quote?.payment_mode ?? props.message.type);
const isInternalPayment = computed(() => isInternalQuotePayment(effectivePaymentMode.value));

const snapshotQuotePaymentStatus = computed(() => props.message.payment_status ?? null);
const isInternalPaymentStatusUpdate = computed(
  () =>
    (props.message.payment_actor_type === "client" || isInternalPayment.value) &&
    !!snapshotQuotePaymentStatus.value &&
    snapshotQuotePaymentStatus.value !== "unpaid",
);
const isSnapshotTerminalQuote = computed(
  () => props.message.status === "cancelled" || props.message.status === "rejected",
);
const isSnapshotQuoteStatusUpdate = computed(
  () =>
    props.message.status === "accepted" ||
    props.message.payment_status === "partial_paid" ||
    props.message.payment_status === "paid" ||
    props.message.payment_status === "partial_refund" ||
    props.message.payment_status === "refund" ||
    props.message.payment_status === "failed",
);
const quoteStatusLabel = computed(() => {
  if (isInternalPaymentStatusUpdate.value) {
    switch (snapshotQuotePaymentStatus.value) {
      case "partial_paid":
        return "made a partial payment through Shopexperts payments";
      case "paid":
        return "paid through Shopexperts payments";
      case "partial_refund":
        return "received a partial refund through Shopexperts payments";
      case "refund":
        return "received a refund through Shopexperts payments";
      case "failed":
        return "had a Shopexperts payment fail";
    }
  }

  switch (props.message.status) {
    case "cancelled":
      return "cancelled a quote";
    case "rejected":
      return "declined a quote";
    case "accepted":
      return "accepted a quote";
    default:
      if (snapshotQuotePaymentStatus.value && snapshotQuotePaymentStatus.value !== "unpaid") {
        return `updated payment status to ${formatQuotePaymentStatusLabel(snapshotQuotePaymentStatus.value).toLowerCase()}`;
      }
      return null;
  }
});

const quoteStatusActorName = computed(() => {
  if (isInternalPaymentStatusUpdate.value) {
    return clientDisplayName.value;
  }
  return sender.value.name;
});

const bubbleClass = computed(() => {
  if (isNote.value) {
    if (props.message.kind === "personal_note") {
      return "bg-violet-50 border-violet-200";
    }
    if (props.message.kind === "admin_note") {
      return "bg-sky-50 border-sky-200";
    }
    return "bg-amber-50 border-amber-200";
  }
  return props.isOwn
    ? "bg-se-gray-100 border-se-gray-300"
    : "bg-white border-se-gray-300";
});

const contentTextClass = computed(() => {
  if (isNote.value) return "text-gray-800";
  return "text-gray-800";
});

const timeClass = computed(() => {
  if (isNote.value) return "text-gray-400";
  return props.isOwn ? "text-gray-400" : "text-gray-400";
});

const senderNameClass = computed(() => {
  if (isNote.value) {
    if (props.message.kind === "personal_note") return "text-violet-700";
    if (props.message.kind === "admin_note") return "text-sky-700";
    return "text-amber-700";
  }
  return "text-gray-600";
});

const noteBadgeClass = computed(() => {
  if (props.message.kind === "personal_note") {
    return "bg-violet-100 text-violet-600 border-violet-200";
  }
  if (props.message.kind === "admin_note") {
    return "bg-sky-100 text-sky-600 border-sky-200";
  }
  return "bg-amber-100 text-amber-600 border-amber-200";
});

const emailReplyBadgeClass = computed(
  () => "bg-sky-50 text-sky-700 border-sky-200",
);

const highlightClass = computed(() =>
  props.isHighlighted
    ? "border-amber-400 ring-2 ring-amber-300/70 shadow-[0_0_0_4px_rgba(251,191,36,0.18)]"
    : ""
);

// The feed's day dividers carry the date, so bubbles show the time; the full date is in the title.
function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function formatFullTimestamp(ts: number): string {
  return new Date(ts).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

const imageAttachments = computed(() =>
  (props.message.attachments ?? []).filter((a) => a.mime_type?.startsWith("image/") && a.url),
);

const fileAttachments = computed(() =>
  (props.message.attachments ?? []).filter((a) => !a.mime_type?.startsWith("image/")),
);

const totalAttachments = computed(
  () => imageAttachments.value.length + fileAttachments.value.length,
);

</script>

<template>
  <ProjectCreatedMessageCard v-if="message.project_card" :project="message.project_card" :side="side" :created-at="message._creationTime" />
  <!-- System Notification -->
  <div v-else-if="isSystem" class="flex justify-center py-2">
    <span class="text-[11px] text-gray-400 tracking-wide">
      {{ message.content }}
    </span>
  </div>

  <!-- Quote Message: status / payment update — collapsible status update -->
  <div
    v-else-if="isQuote && (isSnapshotTerminalQuote || isSnapshotQuoteStatusUpdate)"
    class="flex items-start gap-2.5"
  >
    <div class="w-12 shrink-0 max-sm:hidden" />
    <div class="min-w-0 flex-1 flex" :class="isOwn ? 'justify-end' : 'justify-start'">
      <div
        class="max-w-[min(85%,40rem)] min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 transition-all duration-300 max-sm:max-w-full max-sm:w-full max-sm:rounded-md"
        :class="highlightClass"
      >
        <UiAccordion icon="none">
          <template #trigger="{ open }">
            <div class="flex flex-1 min-w-0 items-baseline gap-1.5 max-sm:flex-col max-sm:items-stretch max-sm:gap-1">
              <UiText as="span" variant="p-small" class="font-semibold text-gray-700 truncate max-sm:hidden">
                {{ quoteStatusActorName }}
              </UiText>
              <UiText as="span" variant="p-small" class="text-gray-500 max-sm:hidden">
                {{ quoteStatusLabel }}
              </UiText>
              <UiIcon
                name="chevron-down"
                class="h-3 w-3 shrink-0 text-gray-400 transition-transform duration-150 max-sm:hidden"
                :class="{ 'rotate-180': open }"
              />
              <span class="ml-auto pl-3 text-[10px] text-gray-400 shrink-0 max-sm:hidden" :title="formatFullTimestamp(message._creationTime)">
                {{ formatTimestamp(message._creationTime) }}
              </span>

              <UiText as="p" variant="p-small" class="hidden min-w-0 leading-snug max-sm:block">
                <span class="font-semibold text-gray-700">{{ quoteStatusActorName }}</span>
                <span class="text-gray-500">&nbsp;{{ quoteStatusLabel }}</span><UiIcon
                  name="chevron-down"
                  class="ml-1 inline-block h-3 w-3 align-[-1px] text-gray-400 transition-transform duration-150"
                  :class="{ 'rotate-180': open }"
                />
              </UiText>
              <span class="hidden text-[10px] leading-none text-gray-400 self-end max-sm:block" :title="formatFullTimestamp(message._creationTime)">
                {{ formatTimestamp(message._creationTime) }}
              </span>
            </div>
          </template>
          <div v-if="quoteStrip" class="pt-3">
            <QuoteDetailStrip :quote="quoteStrip" compact />
            <p
              v-if="quoteStrip.hours_bank_expiration_terms"
              class="mt-2 whitespace-pre-wrap text-h5 leading-relaxed text-gray-600"
            >
              {{ quoteStrip.hours_bank_expiration_terms }}
            </p>
          </div>
        </UiAccordion>
      </div>
    </div>
    <div class="w-12 shrink-0 max-sm:hidden" />
  </div>

  <!-- Quote Message: offered / accepted — full detail card -->
  <div
    v-else-if="isQuote"
    class="flex items-start gap-2.5"
  >
    <div class="w-12 shrink-0 max-sm:hidden" />
    <div class="min-w-0 flex-1">
      <QuoteMessageCard
        :message="message"
        :side="side"
        :is-own="isOwn"
        :sender-name="sender.name"
        :sender-avatar="sender.avatar"
        :sender-initials="sender.initials"
        :expert-role="expertRole"
        :quote="quote"
        :highlight-class="highlightClass"
        :auto-open-details="autoOpenProposalDetails"
        :reject-quote="rejectQuote"
        :view-quote="viewQuote"
      />
    </div>
    <div class="w-12 shrink-0 max-sm:hidden" />
  </div>

  <!-- Regular / Note Messages -->
  <div
    v-else
    class="flex items-start gap-2.5"
    :class="isOwn ? 'flex-row-reverse max-sm:flex-row' : ''"
  >
    <!-- Avatar column -->
    <div v-if="showSender" class="shrink-0 pt-0.5 max-sm:hidden">
      <UiAvatar
        :url="sender.avatar"
        :initials="sender.initials"
        :name="sender.name"
        size="xs"
        :responsive="false"
        color-coded
      />
    </div>
    <div v-else class="w-12 shrink-0 max-sm:hidden" />

    <!-- Bubble -->
    <div class="max-w-[min(75%,36rem)] min-w-0 max-sm:max-w-full max-sm:flex-1">
      <div
        class="rounded-xl border px-3.5 py-2.5 transition-all duration-300 max-sm:rounded-md"
        :class="[bubbleClass, highlightClass]"
      >
        <!-- Sender row (inside bubble) -->
        <div
          v-if="showSender || isNote || isEmailReply"
          class="flex items-center gap-2 mb-1.5"
        >
          <UiAvatar
            v-if="showSender"
            :url="sender.avatar"
            :initials="sender.initials"
            :name="sender.name"
            size="xxxs"
            :responsive="false"
            color-coded
            class="hidden max-sm:inline-flex shrink-0"
          />
          <span
            v-if="showSender"
            class="text-[12px] font-semibold truncate"
            :class="senderNameClass"
          >
            {{ sender.name }}
          </span>
          <span
            v-if="isNote && noteLabel"
            class="inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium border"
            :class="noteBadgeClass"
          >
            {{ noteLabel }}
          </span>
          <span
            v-if="isEmailReply"
            class="inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium border"
            :class="emailReplyBadgeClass"
          >
            Replied by email
          </span>
        </div>

        <!-- Content -->
        <div
          v-if="hasHtml"
          class="se-message-prose overflow-x-auto"
          :class="contentTextClass"
          v-html="message.content"
        />
        <p v-else class="whitespace-pre-wrap text-h5 leading-[1.65]" :class="contentTextClass">
          {{ message.content }}
        </p>

        <!-- Attachments -->
        <div v-if="totalAttachments > 0" class="mt-3 pt-1">
          <div class="mb-2 flex items-center gap-2">
            <UiText as="p" variant="caption-normal" class="shrink-0 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-400">
              Attachments ({{ totalAttachments }})
            </UiText>
            <div class="h-px flex-1 bg-black/4" />
          </div>

          <div v-if="imageAttachments.length > 0" class="flex flex-wrap gap-1.5">
            <a
              v-for="att in imageAttachments"
              :key="att.media_id"
              :href="att.url"
              target="_blank"
              rel="noopener"
              class="group/img relative block overflow-hidden rounded-lg border border-black/5"
            >
              <img
                :src="att.url"
                :alt="att.name || 'Image'"
                class="h-16 w-16 object-cover transition-transform duration-150 group-hover/img:scale-105"
              />
              <div class="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-inset ring-black/5 transition-all group-hover/img:ring-black/10" />
            </a>
          </div>

          <div
            v-if="fileAttachments.length > 0"
            class="flex flex-wrap gap-1.5"
            :class="imageAttachments.length > 0 ? 'mt-2' : ''"
          >
            <a
              v-for="att in fileAttachments"
              :key="att.media_id"
              :href="att.url || '#'"
              target="_blank"
              rel="noopener"
              class="group/file inline-flex max-w-[220px] items-center gap-2 rounded-lg border border-gray-200/80 bg-white/90 px-2.5 py-2 text-[11px] text-gray-600 shadow-[0_1px_2px_rgb(0_0_0/0.04)] transition-all hover:border-gray-300 hover:bg-white hover:shadow-[0_2px_4px_rgb(0_0_0/0.06)]"
            >
              <svg class="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover/file:text-gray-500" viewBox="0 0 16 16" fill="currentColor">
                <path d="M4 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5.414a1 1 0 0 0-.293-.707L10.293 1.293A1 1 0 0 0 9.586 1H4zm5 1.5v2A1.5 1.5 0 0 0 10.5 6H13v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h5v.5z" />
              </svg>
              <span class="truncate font-medium">{{ att.name || att.media_id }}</span>
            </a>
          </div>
        </div>

        <!-- Timestamp (inside bubble, bottom-right) -->
        <div class="mt-1.5 flex justify-end">
          <span class="text-[10px] leading-none" :class="timeClass" :title="formatFullTimestamp(message._creationTime)">
            {{ formatTimestamp(message._creationTime) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
